import { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { containsSpecialChars, containsWhitespace, getCharacterLimitMessage, getInvalidRangeErrorMessage, getSpecialCharacterMessage, handleArrowKeyDown, onKeyDown, preventEnterKey, validateInputRange, validateNumberWithoutDecimal } from '../../../utils/commonFunctions';
import { CloseSvg, RAANPhasingButton, TAPhasingButton, InfoButton } from '../../CommonComponents/svgs/Svgs';
import { VALIDATION_RULES } from '../../../utils/constants';
import { showToastMessage } from '../../../utils/commonFunctions';
import { useSelector } from 'react-redux';
import { MissionStore } from '../../../store/MissionSlice';
import './CreateGroupModel.scss';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import ConfirmationDialog from '../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { CircularProgress } from '@mui/material';


const CreateGroupModel = ({
    loading,
    close,
    satellite_name,
    setSatellite_name,
    satellite_des,
    flagGroup,
    setSatelliteCount,
    satellite_count,
    setSatellite_des,
    onclick,
    type,
    inclination,
    setInclination,
    phasing,
    setPhasing,
    phasingType,
    setPhasingType,
    altitude,
    setAltitude,
    totalSat
}) => {
    const [error, setError] = useState(false);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showPhasingModal, setShowPhasingModal] = useState(false);
    const [phasingDisabled, setPhasingDisabled] = useState(true);
    const [autoPhaseActive, setAutoPhaseActive] = useState(true);
    const [currentKeyEvent, setCurrentKeyEvent] = useState('');
    const hasChangedOnce = useRef(false);
    const originalCountRef = useRef<number | null>(null);
    const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
    const satelliteEdit = useSelector((state: { mission: MissionStore }) => state.mission.satelliteEdit);


    useEffect(() => {
        if (satellite_count > 1) {
            setPhasingDisabled(false)
        } else {
            setPhasingDisabled(true)
            setPhasing(null)
        }
    }, [satellite_count])

    useEffect(() => {
        if (satelliteEdit && satellite_count && originalCountRef.current === null) {
            originalCountRef.current = Number(satellite_count);
        }
        if (satelliteEdit) {
            if (satellite_count > 1) {
                setAutoPhaseActive(false)
                hasChangedOnce.current = false;
            } else {
                setAutoPhaseActive(true)
            }
        } else {
            originalCountRef.current = null;
        }
    }, [satelliteEdit, satellite_count])

    const showErrorMessage = (msg: string) => {
        showToastMessage('error', msg)
    };

    const handleSatelliteNameChange = (event) => {
        event.preventDefault();

        const { value } = event.target;
        const trimmedValue = value.trimStart();

        if (containsSpecialChars(value)) {
            return showToastMessage('error', getSpecialCharacterMessage())
        }

        if (trimmedValue.length > 26 && currentKeyEvent !== 'Backspace') { // In case of clonned satellite - name, the text would exceed 26 characters limit
            if (currentKeyEvent !== '') {
                setCurrentKeyEvent('');
            }
            return showToastMessage('error', getCharacterLimitMessage(26))
        }

        setSatellite_name(trimmedValue);
        setError(false);
    };


    const handleSatelliteCountChange = ({ target: { value } }) => {
        value = value.trim();
        const number = Number(value);

        if (!hasChangedOnce.current && autoPhaseActive === false && satelliteEdit) {
            setAutoPhaseActive(true);
            setPhasing((360 / number).toFixed(2))
            hasChangedOnce.current = true; // Mark as changed once
        }

        if (!value) {
            setSatelliteCount(""); // Allow clearing the input
            setPhasing(null)
            return
        }
        if (userData?.['feature-allowed']?.['satelliteGroups'] === 'Yes') {
            const maxLimit = userData?.['feature-allowed']?.['noOfSatellitesMaxLimit'];
            if (Number.isInteger(number) && number > 0) {
                if (number > maxLimit) {
                    showToastMessage('info', `Up to ${maxLimit} satellites can be created. The number entered is above this limit.`)
                    return
                }
                // Edit mode: allow reduction, check limit only for increase
                if (type === 'Update' && originalCountRef.current !== null) {
                    const originalCount = originalCountRef.current;
                    if (number <= originalCount) {
                        // Reducing - always allow
                        setSatelliteCount(number);
                        if (autoPhaseActive && number > 1) setPhasing((360 / number).toFixed(2))
                        else if (number <= 1) setPhasing(null)
                        return
                    }
                    // Increasing - check limit with adjusted total
                    if (totalSat - originalCount + number > maxLimit) {
                        showToastMessage('info', `Maximum satellite limit is about to be reached. Only ${maxLimit - (totalSat - originalCount)} more satellites can be added.`)
                        return
                    }
                } else if ((number + totalSat) > maxLimit) {
                    showToastMessage('info', `Maximum satellite limit is about to be reached. Only ${(maxLimit - totalSat)} more satellites can be created.`)
                    return
                }
                setSatelliteCount(number);
                if (autoPhaseActive && number > 1) {
                    setPhasing((360 / number).toFixed(2))
                } else if (number <= 1) {
                    setPhasing(null)
                }
                return
            }
            showToastMessage('info', 'Enter a valid satellite count')
        } else if (userData?.['feature-allowed']?.['satelliteGroups'] === 'No') {
            if (Number.isInteger(number) && number > 0) {
                if (number > 1) {
                    showToastMessage('info', `Maximum satellite limit of 1 reached. Contact your Administrator for support.`)
                    return
                }
                setSatelliteCount(number);
                if (autoPhaseActive && number > 1) {
                    setPhasing((360 / number).toFixed(2))
                } else if (number <= 1) {
                    setPhasing(null)
                }
                return
            }
            showToastMessage('info', 'Enter a valid satellite count')
        }

    };

    const handleAltitudeChange = (event) => {
        const { value } = event.target;

        const validation = validateInputRange(
            'Altitude',
            value,
            VALIDATION_RULES.ORBITAL_ALTITUDE.MIN,
            VALIDATION_RULES.ORBITAL_ALTITUDE.MAX,
            VALIDATION_RULES.ORBITAL_ALTITUDE.INPUT_LENGTH,
            VALIDATION_RULES.ORBITAL_ALTITUDE.PARAMETER
        );

        validation.success ? setAltitude(validation.value) : showToastMessage('info', validation.error);

    };


    const handleInclinationChange = (event) => {
        const { value } = event.target;

        const validation = validateInputRange(
            'Inclination',
            value,
            VALIDATION_RULES.ORBITAL_INCLINATION.MIN,
            VALIDATION_RULES.ORBITAL_INCLINATION.MAX,
            VALIDATION_RULES.ORBITAL_INCLINATION.INPUT_LENGTH,
            VALIDATION_RULES.ORBITAL_INCLINATION.PARAMETER
        );

        validation.success ? setInclination(validation.value) : showToastMessage('info', validation.error);
    };


    const handleDescriptionChange = (event) => {
        event.preventDefault();
        const { value } = event.target;

        value.length <= 200
            ? setSatellite_des(value.trimStart())
            : showToastMessage('error', getCharacterLimitMessage(200))

    };


    const handleClick = () => {
        const trimmedName = satellite_name?.trim();

        // Empty or whitespace check (existing)
        if (containsWhitespace(satellite_name) || !trimmedName) {
            setError(true);
            return showToastMessage('info', "Please enter Title");
        }

        // Block ".", "..", "..."
        if (/^\.+$/.test(trimmedName)) {
            setError(true);
            return showToastMessage('info', "Title cannot consist only of dots.");
        }

        if (!altitude || altitude.toString().trim() === "" || altitude < VALIDATION_RULES.ORBITAL_ALTITUDE.MIN || altitude > VALIDATION_RULES.ORBITAL_ALTITUDE.MAX) {
            return showToastMessage('info', (altitude.toString().trim() === "" || (!altitude && altitude !== 0)) ? 'Please enter Altitude' : getInvalidRangeErrorMessage('Altitude', VALIDATION_RULES.ORBITAL_ALTITUDE.MIN, VALIDATION_RULES.ORBITAL_ALTITUDE.MAX, VALIDATION_RULES.ORBITAL_ALTITUDE.PARAMETER));
        }

        if ((!satellite_count || satellite_count.toString().trim() === "")) {
            return showToastMessage('info', "Please enter number of satellites");
        }

        if ((!inclination && inclination !== 0) || inclination.toString().trim() === "" || inclination < VALIDATION_RULES.ORBITAL_INCLINATION.MIN || inclination > VALIDATION_RULES.ORBITAL_INCLINATION.MAX) {
            return showToastMessage('info', (inclination.toString().trim() === "" || (!inclination && inclination !== 0)) ? 'Please enter Inclination' : getInvalidRangeErrorMessage('Inclination', VALIDATION_RULES.ORBITAL_INCLINATION.MIN, VALIDATION_RULES.ORBITAL_INCLINATION.MAX, VALIDATION_RULES.ORBITAL_INCLINATION.PARAMETER));
        }

        if (satellite_count > 1 && (phasing < VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MIN || phasing > VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MAX || !phasing)) {
            return showToastMessage('info', !phasing ? 'Please enter Phasing Angle' : getInvalidRangeErrorMessage('Phasing', VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MIN, VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MAX, VALIDATION_RULES.PLANE_SEPARATION_ANGLE.PARAMETER));
        }
        sessionStorage.setItem('satelliteMode', type === 'Create' ? 'created' : 'updated')
        onclick();
    };


    const handlePhasingChange = (event) => {
        let { value } = event.target;

        if (value.includes('.') && value.split('.')[1].length > 2) {
            showToastMessage('info', 'Phasing cannot exceed two decimal places')
            return;
        }

        // Ensure the value is a valid number and limit to 2 decimal places
        if (!isNaN(value) && value !== '') {
            value = parseFloat(value).toFixed(2);
        }

        const validation = validateInputRange(
            'Phasing',
            value,
            VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MIN,
            VALIDATION_RULES.PLANE_SEPARATION_ANGLE.MAX,
            VALIDATION_RULES.PLANE_SEPARATION_ANGLE.INPUT_LENGTH,
            VALIDATION_RULES.PLANE_SEPARATION_ANGLE.PARAMETER
        );

        validation.success ? setPhasing(validation.value) : showToastMessage('info', validation.error)

    };

    const handlePhasingTypeChange = (e) => {
        e.preventDefault();
        if (phasingType === 'RAAN') {
            setPhasingType('TA')
        } else {
            setPhasingType('RAAN')
        }
    }

    const handleAutoPhase = (e) => {
        e.preventDefault();
        if (!autoPhaseActive && satellite_count) {
            setPhasing((360 / satellite_count).toFixed(2))
        } else {
            setPhasing(null)
        }
        if (autoPhaseActive === false && satelliteEdit) {
            hasChangedOnce.current = true; // Prevent further auto activation
        }
        setAutoPhaseActive(!autoPhaseActive)
    }

    const showToastOnDecimal = (e) => {
        if (e.key === "." || e.key === "Decimal") {
            showToastMessage('info', "Number of Satellites must be a whole number");
        }
    };

    return (
        <div className='admin-form'>
            <div className="createGroupCardSmall admin-form-card">
                <div className="createGroupHeader createGroupBorderBottom">
                    <div className='fs-18'>
                        {type === "Update" ? "Edit Satellite(s)" : "Create Satellite(s)"}
                    </div>
                    <div className='cursor-pointer' onClick={() => {
                        if (loading) return
                        setIsShowModal(true);
                    }}>
                        <CloseSvg />
                    </div>
                </div>
                <Form validated={error} className="createGroupDetailsFormRow createGroupDetailsBoxRow">
                    <div className="createGroupDetailsFormDivGroup createGroupDetailsBoxDiv ">
                        <div className="createGroupDetailsFormItem">
                            <div className="create-group-model__label">Title<span className="opacity-75">*</span></div>
                            <div className="d-flex">
                                <Form.Control
                                    required
                                    type="text"
                                    placeholder="Title"
                                    value={satellite_name}
                                    onChange={handleSatelliteNameChange}
                                    onKeyDown={(event) => setCurrentKeyEvent(event.key)}
                                    onBlur={() => setSatellite_name(satellite_name?.trim())}
                                />
                            </div>
                        </div>
                        {<div className={`createGroupDetailsFormItem ${!flagGroup && 'disabled'}`}>
                            <div className="create-group-model__label d-flex" >No. of Satellites* &nbsp;
                                <span
                                    className="position-relative d-inline-block align-middle"
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                >
                                    <InfoButton />
                                    {isHovered && (
                                        <span className="createGroupDetailsInfoHoverCard">
                                            All the satellites created will be identical.
                                        </span>
                                    )}
                                </span>
                            </div>
                            <Form.Control
                                type="number"
                                required
                                disabled={!flagGroup}
                                min={1}
                                max={50}
                                step={1}
                                value={satellite_count ? satellite_count : ''}
                                onKeyDown={(e) => {
                                    validateNumberWithoutDecimal(e);
                                    showToastOnDecimal(e);
                                }}
                                onChange={handleSatelliteCountChange}
                                placeholder="No. of Satellites"
                                style={{ opacity: !flagGroup ? '0.5' : '' }}
                            />
                        </div>}
                        <div className="createGroupDetailsFormItem">
                            <div className="create-group-model__label">Altitude (h)<span className="opacity-75">*</span></div>
                            <div className="d-flex">
                                <Form.Control
                                    type="number"
                                    required
                                    min={300}
                                    max={2000}
                                    className="inputBoxWithUnitcreateGroup"
                                    value={altitude}
                                    onChange={handleAltitudeChange}
                                    onKeyDown={(error) => handleArrowKeyDown(error, altitude, "Altitude", VALIDATION_RULES?.ORBITAL_ALTITUDE, showToastMessage)}
                                    placeholder="Altitude"
                                    style={{ borderRadius: '4px 0px 0px 4px !important' }}
                                />
                                <span className="unitBoxcreateGroup">km</span>
                            </div>
                        </div>
                        <div className="createGroupDetailsFormItem">
                            <div className="create-group-model__label">Inclination (i)<span className="opacity-75">*</span></div>
                            <div className="d-flex">
                                <Form.Control
                                    type="number"
                                    required
                                    min={0}
                                    max={180}
                                    value={inclination}
                                    className="inputBoxWithUnitcreateGroup"
                                    style={{ borderRadius: '4px 0px 0px 4px !important' }}
                                    onChange={handleInclinationChange}
                                    onKeyDown={(error) => handleArrowKeyDown(error, inclination, "Inclination", VALIDATION_RULES?.ORBITAL_INCLINATION, showToastMessage)}
                                    placeholder="Inclination"
                                />
                                <span className="unitBoxcreateGroup f-16">&deg;</span>
                            </div>
                        </div>
                        <div className="createGroupDetailsFormItem fullWidth">
                            <div className='d-flex phasingToggleContainer'>
                                <div className={`create-group-model__label`}>Phasing
                                    <span className='epochInfoIcon ms-1'
                                        onMouseEnter={() => setShowPhasingModal(true)}
                                        onMouseLeave={() => setShowPhasingModal(false)}
                                    >
                                        <InfoButton />
                                    </span>
                                    {showPhasingModal && (
                                        <span className="createGroupDetailsInfoHoverCard phasing">
                                            Phasing is the spacing of satellites within or between orbit planes.
                                        </span>
                                    )}
                                </div>
                                <div className='phasingToggleButtonContainer'>
                                    <span className={`create-group-model__label ${phasingDisabled ? 'disabled' : (phasingType === 'TA') ? 'active' : ''}`}>TA</span>
                                    <button className={`custom__phasing__toggle__btn__container ${phasingType === 'RAAN' ? 'active' : ''}`} onClick={(e) => handlePhasingTypeChange(e)} disabled={phasingDisabled} />
                                    <span className={`create-group-model__label ${phasingDisabled ? 'disabled' : (phasingType === 'RAAN') ? 'active' : ''}`}>RAAN</span>
                                </div>
                            </div>

                            <div className="d-flex phasingContainer">
                                <CustomToolTip title='Equal Phasing'>
                                    <button className={`phasingButton ${(autoPhaseActive && !phasingDisabled) ? 'active' : ''}`} disabled={phasingDisabled} onClick={handleAutoPhase}>
                                        {phasingType === 'RAAN' ?
                                            (
                                                <RAANPhasingButton active={autoPhaseActive && !phasingDisabled} />
                                            ) : (
                                                <TAPhasingButton active={autoPhaseActive && !phasingDisabled} />

                                            )}
                                    </button>
                                </CustomToolTip>

                                <Form.Control
                                    type="number"
                                    required
                                    min={0.1}
                                    max={360}
                                    value={phasing !== null ? phasing : ""}
                                    className={`inputBoxWithUnitcreateGroup ${autoPhaseActive ? 'autoPhased' : ''}`}
                                    style={{ borderRadius: '4px 0px 0px 4px !important' }}
                                    onChange={handlePhasingChange}
                                    onKeyDown={(error) => handleArrowKeyDown(error, phasing, "Phasing", VALIDATION_RULES?.PLANE_SEPARATION_ANGLE, showToastMessage)}
                                    placeholder="Phasing Angle"
                                    disabled={phasingDisabled || autoPhaseActive}
                                />
                                <span className={`unitBoxcreateGroup f-16 ${(phasingDisabled) ? 'disabled' : autoPhaseActive ? 'autoPhased' : ''}`}>&deg;</span>
                            </div>
                        </div>
                        {type !== 'Clone' &&
                            <div className="createGroupDetailsFormItem w-100 mw-100">
                                <div className={`create-group-model__label`}>Description</div>
                                <div className="d-flex">
                                    <Form.Control
                                        className='customDescriptionForGroup'
                                        as="textarea"
                                        placeholder={"Description"}
                                        value={satellite_des}
                                        onChange={handleDescriptionChange}
                                        onKeyDown={preventEnterKey}
                                        style={{ resize: 'none', height: '71px', color: "#8E8E8E" }}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </Form>
                <div className="createGroupBorderBottom w-100 pb-0"></div>
                <div className="createGroupButtonsDiv">
                    <div className="w-100 d-flex align-items-center justify-content-between">
                        <button
                            disabled={loading}
                            type="button"
                            className={`closeModal__btn_createGroup`}
                            onClick={() => {
                                setIsShowModal(true)
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            disabled={loading}
                            type="button"
                            className="createAndConfigureModal_btn_createGroup"
                            onClick={(e) => {
                                e.preventDefault();
                                handleClick()
                            }}
                        >
                            {loading && <CircularProgress size={14} color='inherit' />}
                            {type} {type === 'Clone &' ? '' : ''}
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                type='cancel'
                open={isShowModal ? true : false}
                title={`Discard Satellite Configuration?`}
                description={`Discarding satellite configuration flow will take you back to the list of satellites.`}
                onCancel={() => setIsShowModal(false)}
                onConfirm={() => {
                    close()
                    setIsShowModal(false);
                }}
                confirm_button_text={`Discard`}
            />
        </div>
    );
};

export default CreateGroupModel;